import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sparkles, Loader2, Check, FileText,
  Upload, Link as LinkIcon, Mic, Youtube, Type,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { aiApi, projectsApi } from "../lib/api";
import { PROJECT_TYPES, INPUT_TYPES } from "../lib/sample-data";
import { cn } from "../lib/utils";

const INPUT_ICON = {
  prompt: Type, paste: FileText, url: LinkIcon, youtube: Youtube,
  voice: Mic, pdf: Upload, docx: Upload, pptx: Upload, transcript: FileText, outline: FileText,
};

export default function CreateAI() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [projectType, setProjectType] = useState("book");
  const [inputType, setInputType] = useState("prompt");
  const [source, setSource] = useState("");
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState("Premium · Authoritative");
  const [quality, setQuality] = useState("balanced");
  const [loading, setLoading] = useState(false);
  const [outline, setOutline] = useState(null);
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);

  const generate = async () => {
    setLoading(true);
    setOutline(null);
    try {
      const res = await aiApi.generate({
        project_type: projectType,
        input_type: inputType,
        source: source || `New ${projectType} project for ${audience || "professional audience"}.`,
        audience,
        tone,
        quality,
      });
      setOutline(res.outline);
      setMeta(`${res.provider} · ${res.model}`);
      setStep(3);
    } catch (e) {
      setOutline(
        `# Generation unavailable\n\nThe AI service couldn't be reached. ` +
          `You can still continue and edit a draft outline manually.`
      );
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const saveAsProject = async () => {
    setCreating(true);
    try {
      const proj = await projectsApi.create({
        title: deriveTitle(outline) || `New ${projectType} project`,
        type: projectType,
        description: (audience && `Audience: ${audience}`) || "",
      });
      nav(`/projects?focus=${proj.id}`);
    } catch (e) {
      nav("/projects");
    } finally {
      setCreating(false);
    }
  };

  const steps = ["Output", "Source", "Brief", "Outline"];

  return (
    <div data-testid="create-ai-page" className="space-y-8">
      <PageHeader
        kicker="Universal Create AI"
        title="What do you want to create?"
        description="Choose an output, give it a source, answer a few quick questions. AI generates the outline. You approve, then we draft."
      />

      {/* Step indicator */}
      <div data-testid="create-steps" className="flex items-center gap-2 flex-wrap">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={cn(
                "h-7 w-7 rounded-full grid place-items-center text-[11px] font-semibold border",
                i < step
                  ? "bg-[var(--pl-aqua)] text-[#061414] border-[var(--pl-aqua)]"
                  : i === step
                  ? "bg-[var(--pl-aqua)]/10 text-[var(--pl-aqua)] border-[var(--pl-aqua)]/40"
                  : "border-[var(--pl-border)] text-[var(--pl-text-muted)]"
              )}
            >
              {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={cn("text-xs", i === step ? "text-[var(--pl-text)]" : "text-[var(--pl-text-muted)]")}>
              {s}
            </span>
            {i < steps.length - 1 && (
              <span className="w-6 h-px bg-[var(--pl-border)]" />
            )}
          </div>
        ))}
      </div>

      {/* Step 0: Project Type */}
      {step === 0 && (
        <div data-testid="step-output" className="space-y-3">
          <div className="text-sm text-[var(--pl-text-muted)]">Pick an output</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {PROJECT_TYPES.map((t) => (
              <button
                key={t.id}
                data-testid={`output-${t.id}`}
                onClick={() => setProjectType(t.id)}
                className={cn(
                  "rounded-2xl p-5 text-left border transition pl-lift",
                  projectType === t.id
                    ? "border-[var(--pl-aqua)] bg-[var(--pl-aqua)]/8"
                    : "border-[var(--pl-border)] bg-[var(--pl-surface)]/60"
                )}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Output</div>
                <div className="font-display text-lg font-medium text-[var(--pl-text)] mt-1">{t.label}</div>
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <NextBtn onClick={() => setStep(1)} />
          </div>
        </div>
      )}

      {/* Step 1: Input Type */}
      {step === 1 && (
        <div data-testid="step-source" className="space-y-3">
          <div className="text-sm text-[var(--pl-text-muted)]">Where should the AI start?</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {INPUT_TYPES.map((t) => {
              const I = INPUT_ICON[t.id] || Sparkles;
              return (
                <button
                  key={t.id}
                  data-testid={`input-${t.id}`}
                  onClick={() => setInputType(t.id)}
                  className={cn(
                    "rounded-2xl p-4 text-left border transition pl-lift flex items-start gap-3",
                    inputType === t.id
                      ? "border-[var(--pl-aqua)] bg-[var(--pl-aqua)]/8"
                      : "border-[var(--pl-border)] bg-[var(--pl-surface)]/60"
                  )}
                >
                  <div className="h-9 w-9 rounded-lg grid place-items-center bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30">
                    <I className="h-4 w-4 text-[var(--pl-aqua)]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">Source</div>
                    <div className="text-sm font-medium text-[var(--pl-text)]">{t.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-between pt-2">
            <BackBtn onClick={() => setStep(0)} />
            <NextBtn onClick={() => setStep(2)} />
          </div>
        </div>
      )}

      {/* Step 2: Brief */}
      {step === 2 && (
        <div data-testid="step-brief" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Source / Brief" required>
              <textarea
                data-testid="brief-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                rows={10}
                placeholder={inputType === "url"
                  ? "Paste a URL the AI should learn from…"
                  : "Tell the AI what to build. The clearer the brief, the better the output."
                }
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="w-full rounded-xl border border-[var(--pl-border)] p-3 text-sm placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Audience">
                <input
                  data-testid="brief-audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g. SaaS founders, $1M+ ARR"
                  style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                  className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
                />
              </Field>
              <Field label="Tone">
                <input
                  data-testid="brief-tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                  className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
                />
              </Field>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="AI Quality">
              <div className="grid grid-cols-2 gap-2">
                {["fast", "balanced", "premium", "custom"].map((q) => (
                  <button
                    key={q}
                    data-testid={`quality-${q}`}
                    onClick={() => setQuality(q)}
                    className={cn(
                      "h-10 rounded-xl text-xs capitalize border transition",
                      quality === q
                        ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)] border-[var(--pl-aqua)]/40"
                        : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
                    )}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </Field>
            <div className="rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-aqua)]/5 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-1">
                Routing preview
              </div>
              <div className="text-sm text-[var(--pl-text)]">
                Output: <span className="capitalize">{projectType}</span>
              </div>
              <div className="text-xs text-[var(--pl-text-muted)]">
                Source: {INPUT_TYPES.find((i) => i.id === inputType)?.label} · Quality: {quality}
              </div>
            </div>
            <button
              data-testid="generate-btn"
              onClick={generate}
              disabled={loading}
              className="w-full h-12 rounded-xl pl-btn-aqua inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Generating outline…" : "Generate with AI"}
            </button>
            <BackBtn onClick={() => setStep(1)} className="w-full" />
          </div>
        </div>
      )}

      {/* Step 3: Outline */}
      {step === 3 && (
        <div data-testid="step-outline" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/80 p-6 backdrop-blur-md">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">
              <Sparkles className="h-3.5 w-3.5" /> AI Outline {meta && `· ${meta}`}
            </div>
            <pre
              data-testid="outline-content"
              className="mt-3 whitespace-pre-wrap font-sans text-sm text-[var(--pl-text)] leading-relaxed"
            >
              {outline}
            </pre>
          </div>
          <div className="space-y-3">
            <button
              data-testid="approve-btn"
              onClick={saveAsProject}
              disabled={creating}
              className="w-full h-12 rounded-xl pl-btn-aqua inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve & Save Project
            </button>
            <button
              data-testid="regen-btn"
              onClick={generate}
              disabled={loading}
              className="w-full h-11 rounded-xl border border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/40 text-sm text-[var(--pl-text)] inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4" /> Regenerate
            </button>
            <BackBtn onClick={() => setStep(2)} className="w-full" />
          </div>
        </div>
      )}
    </div>
  );
}

function deriveTitle(text) {
  if (!text) return null;
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  // 1) Look for "Title:" or "- Title:" pattern (skip the section header "Title & Subtitle")
  for (const l of lines) {
    const m = l.match(/^(?:[-*•\d.\s]*)?title\s*[:\-—]\s*["“]?([^"”]{4,120})["”]?$/i);
    if (m && !/subtitle/i.test(m[1])) return m[1].trim().slice(0, 80);
  }
  // 2) Look for the first quoted phrase
  const q = text.match(/["“]([^"”]{4,80})["”]/);
  if (q) return q[1].trim();
  // 3) First non-heading content line
  const firstContent = lines.find(
    (l) => l.length > 4 && !/^#{1,6}\s/.test(l) && !/^\d+\.\s*(title|positioning|outline|cta|tone)/i.test(l)
  );
  return firstContent?.replace(/^[#*\-\d.\s]+/, "").trim().slice(0, 80) || null;
}

function Field({ label, required, children }) {
  return (
    <label className="block space-y-1.5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
        {label} {required && <span className="text-[var(--pl-lime)]">*</span>}
      </div>
      {children}
    </label>
  );
}

function NextBtn({ onClick }) {
  return (
    <button
      data-testid="step-next"
      onClick={onClick}
      className="inline-flex items-center gap-2 h-11 px-4 rounded-xl pl-btn-aqua text-sm"
    >
      Continue <ArrowRight className="h-4 w-4" />
    </button>
  );
}

function BackBtn({ onClick, className }) {
  return (
    <button
      data-testid="step-back"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[var(--pl-border)] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40",
        className
      )}
    >
      <ArrowLeft className="h-4 w-4" /> Back
    </button>
  );
}
